import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import useCandidateTagsApi, { DEFAULT_TAG_COLOR } from './config/candidateTags';

// ATS Ayarları > Aday Etiketleri — kiracının etiket KATALOĞU (ad + renk). Adaylara
// atama aday detay sayfasındaki Etiketler sekmesinden yapılır; aday listesi ve havuz
// üyeleri `tagId[in]` ile bu katalog üzerinden süzülür. Sayfa zaten AT.Write rotasıdır.
const Hint = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin: 0 0 12px;
`;

export default function CandidateTagsTab() {
  const { t } = useTranslation();
  const { canWrite } = useAuth() || {};
  const canManage = canWrite ? canWrite('AT') : false;

  const [queryParams, setQueryParams] = useState({ pageSize: 20, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [drawer, setDrawer] = useState({ open: false, row: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, row: null });
  const [busy, setBusy] = useState(false);

  const {
    fetching, loadFailed, data, pagination, columns: baseColumns, objectSchema, create, update, remove,
  } = useCandidateTagsApi(queryParams);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((next) => setFilterList(next), []);

  const openNew = useCallback(() => setDrawer({ open: true, row: null }), []);
  const openEdit = useCallback((row) => setDrawer({ open: true, row }), []);

  const actionOptions = useMemo(() => {
    if (!canManage) return [];
    return [
      { type: 'edit', label: t('common.edit'), onClick: openEdit },
      { type: 'divider' },
      { type: 'delete', label: t('common.delete'), onClick: (row) => setDeleteConfirm({ isOpen: true, row }) },
    ];
  }, [canManage, t, openEdit]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return null;
            return (
              <span onClick={(e) => e.stopPropagation()} role="presentation">
                <OptionsMenu actions={actionOptions} row={row} />
              </span>
            );
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  const initialValues = useMemo(() => (drawer.row
    ? { name: drawer.row.name || '', color: drawer.row.color || DEFAULT_TAG_COLOR }
    : null), [drawer.row]);

  const handleSave = useCallback(async (values) => {
    const ok = drawer.row ? await update(drawer.row.id, values) : await create(values);
    if (ok) setDrawer({ open: false, row: null });
    return ok;
  }, [drawer.row, update, create]);

  const confirmDelete = useCallback(async () => {
    if (!deleteConfirm.row) return;
    setBusy(true);
    await remove(deleteConfirm.row.id);
    setBusy(false);
    setDeleteConfirm({ isOpen: false, row: null });
  }, [remove, deleteConfirm.row]);

  const usage = deleteConfirm.row?.usageCount || 0;

  return (
    <div data-role="ats-candidate-tags-tab">
      <Hint>{t('ats.candidateTag.catalogHint')}</Hint>

      {loadFailed && <LoadErrorBanner message={t('ats.candidateTag.loadFailed')} />}

      <GenericFormDrawer
        open={drawer.open}
        onClose={() => setDrawer({ open: false, row: null })}
        title={drawer.row ? t('ats.candidateTag.edit') : t('ats.candidateTag.new')}
        schema={objectSchema}
        initialValues={initialValues}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('common.delete')}
        message={usage > 0
          ? t('ats.candidateTag.deleteInUseConfirm', { name: deleteConfirm.row?.name || '', count: usage })
          : t('ats.candidateTag.deleteConfirm', { name: deleteConfirm.row?.name || '' })}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, row: null })}
        loading={busy}
      />

      <DataTable
        title={t('ats.candidateTag.catalogTitle')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord={canManage}
        newButtonLabel={t('ats.candidateTag.new')}
        onNewButtonClick={openNew}
        onRowClick={canManage ? openEdit : undefined}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
      />
    </div>
  );
}
